import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarCheck,
  Check,
  ChevronDown,
  ListChecks,
  Loader2,
  Pencil,
  RotateCw,
  Sparkles,
  Upload,
} from "lucide-react";

import { GRANT_ARTIFACT_ROLE_TO_TYPE } from "@/api/grant_artifacts";
import {
  GRANT_OPPORTUNITY_DOCUMENT_ROLE_LABELS,
  linkGrantOpportunityDocument,
  listGrantOpportunityDocuments,
  retryGrantOpportunityDocumentProcessing,
  unlinkGrantOpportunityDocument,
  type GrantOpportunityDocumentLink,
  type GrantOpportunityDocumentRole,
} from "@/api/grant_opportunity_documents";
import {
  listGrantTaskExtractions,
  type GrantTaskExtractionFocus,
  type GrantTaskExtractionSummary,
} from "@/api/grant_opportunity_task_extractions";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { GrantApplicationDraftEditor } from "@/components/grant_suite/GrantApplicationDraftEditor";
import { GrantDocumentUploadDialog } from "@/components/grant_suite/GrantDocumentUploadDialog";
import { GrantOpportunitySection } from "@/components/grant_suite/GrantOpportunitySection";
import { GrantTaskExtractionDialog } from "@/components/grant_suite/GrantTaskExtractionDialog";
import {
  useWorkflowDocumentLinks,
  WORKFLOW_DOCUMENT_LINK_HELPER_TEXT,
  WorkflowDocumentLinkButton,
  WorkflowDocumentLinkList,
  WorkflowDocumentLinkPickerDialog,
  WorkflowDocumentLinksBlocked,
} from "@/components/workflow_tracking/WorkflowDocumentLinks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";

type GrantOpportunityDocumentsProps = {
  municipalityId: number;
  opportunityId: number;
  opportunityTitle?: string;
  fileViewerEnabled: boolean;
  /** The grant's File Center folder — its documents surface first in the link picker. */
  documentFolderId?: number | null;
  embedded?: boolean;
};

const GRANT_DOCUMENT_LINK_HELPER_TEXT = `${WORKFLOW_DOCUMENT_LINK_HELPER_TEXT} Application drafts you create here are saved under Grants.`;

const AI_ACTIONS: Array<{
  focus: GrantTaskExtractionFocus;
  label: string;
  Icon: typeof Sparkles;
}> = [
  { focus: "reporting", label: "Extract requirements", Icon: Sparkles },
  { focus: "milestone", label: "Generate milestones", Icon: CalendarCheck },
  { focus: "compliance", label: "Generate compliance checklist", Icon: ListChecks },
];

// ENG-1982: statuses of a knowledge-base upload still moving through the
// ingest pipeline (parse, chunk, embed) on its way to searchable.
const BUSY_PROCESSING_STATUSES: ReadonlySet<GrantOpportunityDocumentLink["processing_status"]> =
  new Set(["queued", "uploading", "parsing", "retrying"]);
const PROCESSING_POLL_MS = 4000;

// Artifact draft roles are assigned when saving a generated draft, not via
// manual linking; package files only enter through the planning modal.
const GRANT_ROLE_OPTIONS = Object.entries(GRANT_OPPORTUNITY_DOCUMENT_ROLE_LABELS)
  .filter(([value]) => !(value in GRANT_ARTIFACT_ROLE_TO_TYPE) && value !== "package_source")
  .map(([value, label]) => ({ value, label }));

export function GrantOpportunityDocuments({
  municipalityId,
  opportunityId,
  opportunityTitle = "Grant opportunity",
  fileViewerEnabled,
  documentFolderId = null,
  embedded = false,
}: GrantOpportunityDocumentsProps) {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  // Outside collaborators read documents but never upload, link, unlink,
  // edit drafts, or run AI extractions (ENG-1877).
  const outsideUser = userIsOutsideUser(currentUser);
  const [editingDraftLink, setEditingDraftLink] = useState<{
    id: number;
    role: GrantOpportunityDocumentRole;
  } | null>(null);
  const [extractingDoc, setExtractingDoc] = useState<{
    id: number;
    name: string;
    focus: GrantTaskExtractionFocus;
  } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Memoized so the processing poll below can depend on it directly instead of
  // repeating the key, which would silently stop settling the status badge if
  // the key ever gains a segment.
  const documentsQueryKey = useMemo(
    () => ["grant_opportunity_documents", municipalityId, opportunityId] as const,
    [municipalityId, opportunityId],
  );
  const extractionsQueryKey = [
    "grant_opportunity_task_extractions",
    municipalityId,
    opportunityId,
  ] as const;

  // Latest completed run per (document, focus), so the AI actions menu can
  // flag what already ran for a file (ENG-1721).
  const { data: extractionHistory = [] } = useQuery({
    queryKey: extractionsQueryKey,
    queryFn: () => listGrantTaskExtractions(municipalityId, opportunityId),
    // Extractions read is denied for outside users, and the AI actions menu
    // that consumes this history is hidden for them anyway.
    enabled: fileViewerEnabled && !outsideUser,
  });
  const extractionByDocFocus = useMemo(() => {
    const map = new Map<string, GrantTaskExtractionSummary>();
    for (const row of extractionHistory) {
      map.set(`${row.document_id}:${row.focus}`, row);
    }
    return map;
  }, [extractionHistory]);

  const links = useWorkflowDocumentLinks<GrantOpportunityDocumentLink>({
    municipalityId,
    linksQueryKey: documentsQueryKey,
    listLinks: () => listGrantOpportunityDocuments(municipalityId, opportunityId),
    linkDocument: (documentId, role) =>
      linkGrantOpportunityDocument(municipalityId, opportunityId, documentId, {
        role: role as GrantOpportunityDocumentRole,
      }),
    unlinkDocument: (linkId) =>
      unlinkGrantOpportunityDocument(municipalityId, opportunityId, linkId),
    defaultRole: "attachment",
    enabled: fileViewerEnabled,
    highlightFolderId: documentFolderId,
  });

  // ENG-1982: a Files-surface upload on an AI Partner opportunity runs
  // through the package ingest pipeline before the Research Assistant can
  // search it. Poll while any link is processing so the status badge settles
  // without a manual refresh; the interval disappears once everything is
  // ready or failed.
  // Outside users are excluded: the status badge rides in the row's action
  // slot, which is suppressed for them, so polling would refetch every 4s to
  // change nothing they can see.
  const anyProcessing =
    !outsideUser &&
    links.links.some((link) => BUSY_PROCESSING_STATUSES.has(link.processing_status));
  useEffect(() => {
    if (!anyProcessing) return;
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: documentsQueryKey });
    }, PROCESSING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [anyProcessing, documentsQueryKey, queryClient]);

  const retryMutation = useMutation({
    mutationFn: (linkId: number) =>
      retryGrantOpportunityDocumentProcessing(municipalityId, opportunityId, linkId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: documentsQueryKey });
    },
  });

  const uploadButton = (
    <Button variant="outline" size="sm" className="shrink-0" onClick={() => setUploadOpen(true)}>
      <Upload className="mr-1.5 h-3.5 w-3.5" />
      Upload file
    </Button>
  );

  const uploadDialog = (
    <GrantDocumentUploadDialog
      open={uploadOpen}
      onOpenChange={setUploadOpen}
      municipalityId={municipalityId}
      opportunityId={opportunityId}
      roleOptions={GRANT_ROLE_OPTIONS}
      onUploaded={() => {
        queryClient.invalidateQueries({ queryKey: documentsQueryKey });
      }}
    />
  );

  const picker = (
    <WorkflowDocumentLinkPickerDialog
      open={links.pickerOpen}
      onOpenChange={links.setPickerOpen}
      description="Choose a library document to attach to this grant opportunity."
      selectedDocumentId={links.selectedDocumentId}
      onSelectedDocumentIdChange={links.setSelectedDocumentId}
      selectedRole={links.selectedRole}
      onSelectedRoleChange={links.setSelectedRole}
      roleOptions={GRANT_ROLE_OPTIONS}
      documentSearch={links.documentSearch}
      onDocumentSearchChange={links.setDocumentSearch}
      searchResults={links.searchResults}
      searchLoading={links.searchLoading}
      searchReady={links.searchReady}
      linking={links.linkMutation.isPending}
      highlightBadgeLabel="In this grant's folder"
      onLink={() =>
        links.linkMutation.mutate({
          documentId: Number(links.selectedDocumentId),
          role: links.selectedRole,
        })
      }
    />
  );

  const draftEditor =
    editingDraftLink != null ? (
      <GrantApplicationDraftEditor
        open
        onOpenChange={(open) => {
          if (!open) setEditingDraftLink(null);
        }}
        municipalityId={municipalityId}
        opportunityId={opportunityId}
        opportunityTitle={opportunityTitle}
        mode="edit"
        linkId={editingDraftLink.id}
        artifactType={GRANT_ARTIFACT_ROLE_TO_TYPE[editingDraftLink.role] ?? null}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: documentsQueryKey });
        }}
      />
    ) : null;

  const extractionDialog =
    extractingDoc != null ? (
      <GrantTaskExtractionDialog
        municipalityId={municipalityId}
        opportunityId={opportunityId}
        document={extractingDoc}
        focus={extractingDoc.focus}
        onOpenChange={(open) => {
          if (!open) {
            setExtractingDoc(null);
            queryClient.invalidateQueries({ queryKey: extractionsQueryKey });
          }
        }}
      />
    ) : null;

  const list = (
    <WorkflowDocumentLinkList
      links={links.links}
      isLoading={links.isLoading}
      error={links.error}
      roleLabels={GRANT_OPPORTUNITY_DOCUMENT_ROLE_LABELS}
      emptyMessage={
        outsideUser
          ? "No documents linked yet."
          : "No documents linked yet. Attach an RFP or supporting file from File Center."
      }
      unlinking={links.unlinkMutation.isPending}
      onUnlink={(linkId) => links.unlinkMutation.mutate(linkId)}
      readOnly={outsideUser}
      renderLinkActions={(link) => (
        <>
          <DocumentProcessingStatus
            link={link}
            retryPending={retryMutation.isPending && retryMutation.variables === link.id}
            onRetry={() => retryMutation.mutate(link.id)}
          />
          {link.role === "application_draft" || link.role in GRANT_ARTIFACT_ROLE_TO_TYPE ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setEditingDraftLink({
                id: link.id,
                role: link.role as GrantOpportunityDocumentRole,
              })
            }
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Edit
          </Button>
        ) : (
          // modal={false}: selecting an item opens the extraction Dialog; the
          // menu's own focus/scroll lock would fight the dialog's during the
          // handoff.
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                AI actions
                <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {AI_ACTIONS.map(({ focus, label, Icon }) => {
                const previousRun = extractionByDocFocus.get(`${link.document_id}:${focus}`);
                return (
                  <DropdownMenuItem
                    key={focus}
                    onSelect={() =>
                      setExtractingDoc({ id: link.document_id, name: link.name, focus })
                    }
                  >
                    <Icon className="mr-1.5 h-3.5 w-3.5" />
                    <span className="flex flex-col">
                      <span>{label}</span>
                      {previousRun && (
                        <span className="text-xs text-muted-foreground">
                          {previousRun.confirmed_at
                            ? `Added to checklist ${formatRelativeTime(previousRun.confirmed_at)}`
                            : `Ran ${formatRelativeTime(previousRun.created_at)}`}
                        </span>
                      )}
                    </span>
                    {previousRun && (
                      <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </>
      )}
    />
  );

  if (!fileViewerEnabled) {
    const blocked = (
      <WorkflowDocumentLinksBlocked
        testId="grant-opportunity-documents"
        message="File Center access is required to attach documents to this opportunity."
      />
    );
    if (embedded) return blocked;
    return (
      <GrantOpportunitySection title="Documents" description="Attach RFPs and supporting files from File Center.">
        {blocked}
      </GrantOpportunitySection>
    );
  }

  if (embedded) {
    return (
      <div className="min-w-0 space-y-4" data-testid="grant-opportunity-documents">
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 text-sm text-muted-foreground">
            {outsideUser ? "Documents the team has attached to this grant." : GRANT_DOCUMENT_LINK_HELPER_TEXT}
          </p>
          {!outsideUser && uploadButton}
          {!outsideUser && (
            <WorkflowDocumentLinkButton className="shrink-0" onClick={links.openPicker} />
          )}
        </div>
        {list}
        {picker}
        {uploadDialog}
        {draftEditor}
        {extractionDialog}
      </div>
    );
  }

  return (
    <GrantOpportunitySection
      title="Documents"
      description={
        outsideUser ? "Documents the team has attached to this grant." : GRANT_DOCUMENT_LINK_HELPER_TEXT
      }
      action={
        outsideUser ? null : (
          <div className="flex items-center gap-2">
            {uploadButton}
            <WorkflowDocumentLinkButton onClick={links.openPicker} />
          </div>
        )
      }
    >
      <div className="space-y-3" data-testid="grant-opportunity-documents">
        {list}
        {picker}
        {uploadDialog}
        {draftEditor}
        {extractionDialog}
      </div>
    </GrantOpportunitySection>
  );
}

// ENG-1982: ingest status for a knowledge-base upload. Renders nothing for
// links outside the pipeline: "ready" covers both a finished ingest and a
// plain file that never entered it.
function DocumentProcessingStatus({
  link,
  retryPending,
  onRetry,
}: {
  link: GrantOpportunityDocumentLink;
  retryPending: boolean;
  onRetry: () => void;
}) {
  if (BUSY_PROCESSING_STATUSES.has(link.processing_status)) {
    return (
      <Badge
        variant="outline"
        className="h-6 shrink-0 gap-1 px-1.5 text-[10px] font-normal text-muted-foreground"
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Processing
      </Badge>
    );
  }
  if (link.processing_status !== "failed") return null;
  return (
    <>
      <Badge
        variant="outline"
        className="h-6 shrink-0 px-1.5 text-[10px] font-normal text-destructive"
        title={link.error_message ?? undefined}
      >
        Processing failed
      </Badge>
      <Button variant="ghost" size="sm" disabled={retryPending} onClick={onRetry}>
        <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Retry
      </Button>
    </>
  );
}
